import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  BsBriefcase, 
  BsCpu, 
  BsRobot, 
  BsCodeSquare, 
  BsGlobe, 
  BsGraphUpArrow, 
  BsPalette, 
  BsCheckCircleFill, 
  BsArrowRight, 
  BsQuestionCircle,
  BsCheck2Circle
} from 'react-icons/bs';
import GlobalCta from '../../Components/GlobalCta/GlobalCta';

const solutionsDatabase = {
  'crm-development': {
    title: 'Custom CRM Solutions & Pipeline Automation',
    badge: 'CRM Engineering',
    headline: 'Manage Leads, Customers, and Sales Pipelines in One Unified Platform.',
    desc: 'We engineer custom CRM platforms tailored around your sales cycles, lead channels, team roles, and customer communication workflows.',
    features: [
      'Visual Lead Pipeline Management',
      'Automated Lead Scoring & Qualification',
      'Multi-Channel Inbound Logging (WhatsApp, Email, Web Forms)',
      'Team Activity Logs & Real-Time SLA Notifications',
      'Custom ERP & Email Marketing Integrations',
      'Advanced Customer Analytics & Revenue Forecasts'
    ],
    process: [
      '01. Workflow Analysis & Pipeline Mapping',
      '02. Database Schema & Role Configuration',
      '03. Custom Dashboard & API Integration',
      '04. Team Onboarding & System Deployment'
    ],
    faqs: [
      { q: 'Can Beta CRM integrate with our existing ERP or accounting software?', a: 'Yes. We build custom API connectors to sync leads, orders, and invoices seamlessly.' },
      { q: 'Is the CRM hosted on our infrastructure or cloud?', a: 'We support both dedicated cloud hosting and on-premise enterprise deployments.' }
    ]
  },
  'erp-development': {
    title: 'Enterprise Resource Planning (ERP) Development',
    badge: 'ERP Architecture',
    headline: 'Unified Operational, Inventory, and Financial Control.',
    desc: 'Replace fragmented spreadsheets and disconnected tools with a custom ERP engine built for your exact operational requirements.',
    features: [
      'Multi-Warehouse Inventory & Supply Chain Tracking',
      'Employee Portal, Attendance & Payroll Management',
      'Financial Accounting, Invoicing & Automated Reconciliation',
      'Multi-Branch Operational Matrix & Reporting',
      'Supplier & Procurement Management',
      'Role-Based Enterprise Access & Security Protocols'
    ],
    process: [
      '01. Enterprise Operational Audit',
      '02. Module Architecture & Database Engineering',
      '03. System Integration & User Acceptance Testing',
      '04. Live Migration & Continuous SLA Support'
    ],
    faqs: [
      { q: 'How long does a custom ERP implementation take?', a: 'Depending on module complexity, deployment ranges from 4 to 12 weeks with phased rollouts.' },
      { q: 'Can we add custom modules later?', a: 'Absoluty. Our modular architecture allows seamless addition of new business modules at any time.' }
    ]
  },
  'ai-automation': {
    title: 'AI Agents & Workflow Automation',
    badge: 'Artificial Intelligence',
    headline: 'Automate Repetitive Work and Scale Customer Engagement 24/7.',
    desc: 'Deploy custom AI agents, WhatsApp Business bots, and automated process pipelines that eliminate manual labor and respond instantly.',
    features: [
      'WhatsApp Business AI Conversation Engines',
      'Document & Knowledge-Base Query AI Assistants',
      'Automated Lead Qualification & CRM Assignment',
      'Custom LLM Integration (OpenAI, Claude, Llama)',
      'API Automation Across SaaS Tools & Databases',
      'Real-Time Multi-Language Customer Support'
    ],
    process: [
      '01. Automation Opportunity Mapping',
      '02. AI Model Training & Prompt Engineering',
      '03. Workflow & Connector Integration',
      '04. Live Monitoring & Continuous Tuning'
    ],
    faqs: [
      { q: 'How secure is our business data with AI agents?', a: 'Data is encrypted in transit and at rest. We enforce strict data privacy and private LLM endpoints.' },
      { q: 'Does WhatsApp AI support human escalation?', a: 'Yes. The AI hands off complex conversations seamlessly to human agents on your CRM.' }
    ]
  },
  'software-development': {
    title: 'Custom Enterprise Software Development',
    badge: 'Software Engineering',
    headline: 'Bespoke Digital Platforms Built for Scale and Performance.',
    desc: 'We engineer secure, scalable, cloud-native web applications and SaaS platforms designed to achieve your commercial goals.',
    features: [
      'Multi-Tenant SaaS Application Architecture',
      'High-Performance REST & GraphQL APIs',
      'Cloud-Native Microservices (AWS, Docker)',
      'Enterprise Data Security & Encryption',
      'Custom Web Portals & Partner Platforms',
      'Legacy System Modernization & Migration'
    ],
    process: [
      '01. Product Requirements & Architecture Blueprint',
      '02. UX/UI Prototyping & Technical Specification',
      '03. Sprint-Based Agile Development',
      '04. Cloud Deployment & CI/CD Pipeline Setup'
    ],
    faqs: [
      { q: 'Who owns the IP and source code?', a: 'You own 100% of the custom source code, IP rights, and database schemas.' }
    ]
  },
  'web-development': {
    title: 'High-Performance Web Solutions',
    badge: 'Web Architecture',
    headline: 'Sub-Second Page Speeds & Enterprise Design Systems.',
    desc: 'We design and build high-converting websites, web apps, and web portals optimized for speed, search engines, and user retention.',
    features: [
      'React & Next.js Modern Frontend Engineering',
      'Sub-Second Page Load Optimization & Core Web Vitals',
      'Enterprise SEO Architecture & Schema Markup',
      'Responsive Across All Mobile & Desktop Breakpoints',
      'Headless CMS & Custom Content Management',
      'Full Security Protection & SSL Compliance'
    ],
    process: [
      '01. Strategy & Content Architecture',
      '02. Wireframing & Responsive Visual Design',
      '03. Frontend Development & Speed Tuning',
      '04. SEO Audit, Launch & Performance Monitoring'
    ],
    faqs: [
      { q: 'Will the website be fully manageable by our team?', a: 'Yes. We provide easy-to-use content management tools and full documentation.' }
    ]
  },
  'digital-marketing': {
    title: 'Digital Growth & Lead Generation',
    badge: 'Growth Systems',
    headline: 'Data-Driven Acquisition & Revenue Acceleration.',
    desc: 'Engineered search engine optimization, paid advertising campaigns, and conversion funnels designed to deliver qualified B2B leads.',
    features: [
      'Technical & Strategic Search Engine Optimization (SEO)',
      'High-Intent Google & Social Paid Campaigns',
      'Conversion Rate Optimization (CRO)',
      'Automated Lead Nurturing & Email Funnels',
      'Competitor Benchmarking & Keyword Strategy',
      'Live ROI Analytics & Conversion Attribution'
    ],
    process: [
      '01. Market Audit & Conversion Assessment',
      '02. Campaign Setup & Funnel Engineering',
      '03. High-Intent Acquisition Deployment',
      '04. A/B Testing & Weekly ROI Optimization'
    ],
    faqs: [
      { q: 'How quickly can we expect leads from digital growth campaigns?', a: 'Paid campaigns generate immediate leads within days, while SEO builds compounding organic momentum.' }
    ]
  },
  'ui-ux-design': {
    title: 'UI/UX & Product Design',
    badge: 'Design Engineering',
    headline: 'Intuitive Interfaces Engineered for High User Engagement.',
    desc: 'We create clean, user-tested visual design systems, interactive prototypes, and digital product interfaces that delight users.',
    features: [
      'User Research & Persona Mapping',
      'Interactive Figma Prototypes & Wireframes',
      'Enterprise UI Component Design Systems',
      'Usability Testing & Conversion Flow Optimization',
      'Responsive Mobile & Web UI Design',
      'Brand Identity & Visual Style Guidelines'
    ],
    process: [
      '01. User Needs & Workflow Discovery',
      '02. Low-Fidelity Wireframes & UX Mapping',
      '03. High-Fidelity Interactive Visual Prototypes',
      '04. Design System Handoff to Development'
    ],
    faqs: [
      { q: 'Do you deliver ready-to-code design assets?', a: 'Yes. We deliver complete Figma design systems with developer handoff specs.' }
    ]
  }
};

const SolutionDetail = () => {
  const { solutionId } = useParams();
  const solution = solutionsDatabase[solutionId] || solutionsDatabase['crm-development'];

  return (
    <div className="bg-slate-950 text-white min-h-screen font-sans selection:bg-[#f06600] selection:text-white pt-10">
      
      {/* Hero Header */}
      <section className="py-16 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-extrabold text-[#f06600] bg-orange-950/60 border border-orange-900/40 px-3.5 py-1.5 rounded-full inline-block mb-4">
            {solution.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-4xl">
            {solution.title}
          </h1>
          <p className="text-xl text-[#f06600] font-semibold mt-3 max-w-3xl">
            {solution.headline}
          </p>
          <p className="text-slate-300 text-base mt-4 max-w-3xl leading-relaxed">
            {solution.desc}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contactus">
              <button className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded-xl shadow-lg shadow-orange-500/25 flex items-center gap-2 text-sm">
                <span>Request Technical Consultation</span>
                <BsArrowRight />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8">
            Core Capabilities & Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solution.features.map((feat, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex items-start gap-3 shadow-sm">
                <BsCheck2Circle className="text-[#f06600] text-xl shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm font-semibold leading-snug">{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8">
            Implementation Process
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {solution.process.map((step, idx) => (
              <div key={idx} className="bg-slate-950 border border-slate-800 p-6 rounded-2xl shadow-sm">
                <span className="text-xs font-bold text-[#f06600] font-mono block mb-2">{step.split('.')[0]}</span>
                <h4 className="text-base font-bold text-white">{step.split('.')[1]}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {solution.faqs.map((faq, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-sm">
                <h4 className="text-base font-bold text-white flex items-center gap-2 mb-2">
                  <BsQuestionCircle className="text-[#f06600]" />
                  <span>{faq.q}</span>
                </h4>
                <p className="text-slate-300 text-sm pl-6 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global CTA */}
      <GlobalCta />

    </div>
  );
};

export default SolutionDetail;
